/**
 * Builder Contacts (TAS-295) — who we can actually reach at each builder.
 * Left: builder list with active-contact counts + search. Right: the selected
 * builder's contacts with add/edit/remove (writes gated on manageWorkOrders;
 * removal is a soft delete server-side). Feeds the WO email flows (TAS-296/297/299).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./WorkOrders.css";
import { AlertTriangle, BookUser, Mail, Pencil, Phone, Plus, Trash2, Users } from "lucide-react";
import { useSiteContext } from "../../../SiteContext";
import { can } from "../../../util/adminPermissions";
import {
    CustomerContact,
    CustomerProject,
    CustomerWithContactCount,
    ProjectContact,
    createCustomerContact,
    createProjectContact,
    deleteCustomerContact,
    deleteProjectContact,
    getCustomerContacts,
    getCustomerProjects,
    getCustomersWithContacts,
    getProjectContacts,
    updateCustomerContact,
    updateProjectContact,
} from "../../../api/customerContacts";
import { Banner, EmptyState, ModalShell, PageHead, SectionLabel } from "./woShared";

type Draft = { name: string; roleTitle: string; email: string; phoneNumber: string; receivesWorkOrderEmails: boolean };
const EMPTY_DRAFT: Draft = { name: "", roleTitle: "", email: "", phoneNumber: "", receivesWorkOrderEmails: false };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CustomerContactsPage() {
    const { userRole } = useSiteContext();
    const canManage = can(userRole, "manageWorkOrders");

    const [customers, setCustomers] = useState<CustomerWithContactCount[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [q, setQ] = useState("");
    const [selectedID, setSelectedID] = useState<number | null>(null);

    const [contacts, setContacts] = useState<CustomerContact[]>([]);
    const [contactsLoading, setContactsLoading] = useState(false);
    const [contactsError, setContactsError] = useState<string | null>(null);

    // TAS-296 — job-level contacts (seeded from the builder on first open).
    const [projects, setProjects] = useState<CustomerProject[]>([]);
    const [selectedProjectID, setSelectedProjectID] = useState<number | null>(null);
    const [projectContacts, setProjectContacts] = useState<ProjectContact[]>([]);
    const [projectSeeded, setProjectSeeded] = useState(false);
    const [projectLoading, setProjectLoading] = useState(false);
    const [projectError, setProjectError] = useState<string | null>(null);

    // Modal scope: 'builder' edits the defaults; 'job' edits only this job's copy.
    const [modal, setModal] = useState<"add" | "edit" | null>(null);
    const [modalScope, setModalScope] = useState<"builder" | "job">("builder");
    const [editTarget, setEditTarget] = useState<CustomerContact | null>(null);
    const [editJobTarget, setEditJobTarget] = useState<ProjectContact | null>(null);
    const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
    const [saving, setSaving] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<CustomerContact | null>(null);

    const fetchCustomers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setCustomers(await getCustomersWithContacts());
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || "Failed to load builders.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void fetchCustomers(); }, [fetchCustomers]);

    const fetchContacts = useCallback(async (customerID: number) => {
        setContactsLoading(true);
        setContactsError(null);
        try {
            setContacts(await getCustomerContacts(customerID));
        } catch (err: any) {
            setContactsError(err?.response?.data?.message || err?.message || "Failed to load contacts.");
        } finally {
            setContactsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (selectedID != null) void fetchContacts(selectedID);
    }, [selectedID, fetchContacts]);

    useEffect(() => {
        setProjects([]);
        setSelectedProjectID(null);
        setProjectContacts([]);
        setProjectSeeded(false);
        if (selectedID == null) return;
        getCustomerProjects(selectedID)
            .then(setProjects)
            .catch(() => setProjects([]));
    }, [selectedID]);

    const fetchProjectContacts = useCallback(async (projectID: number) => {
        setProjectLoading(true);
        setProjectError(null);
        try {
            const r = await getProjectContacts(projectID);
            setProjectContacts(r.contacts);
            setProjectSeeded(r.seeded);
        } catch (err: any) {
            setProjectError(err?.response?.data?.message || err?.message || "Failed to load job contacts.");
        } finally {
            setProjectLoading(false);
        }
    }, []);

    useEffect(() => {
        setProjectContacts([]);
        setProjectSeeded(false);
        if (selectedProjectID != null) void fetchProjectContacts(selectedProjectID);
    }, [selectedProjectID, fetchProjectContacts]);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return customers;
        return customers.filter((c) =>
            [c.name, c.altCustomerID].filter(Boolean).join(" ").toLowerCase().includes(term)
        );
    }, [customers, q]);

    const selected = customers.find((c) => c.customerID === selectedID) ?? null;

    const openAdd = (scope: "builder" | "job") => { setModalScope(scope); setDraft(EMPTY_DRAFT); setModalError(null); setModal("add"); };
    const openEdit = (c: CustomerContact) => {
        setModalScope("builder");
        setEditTarget(c);
        setDraft({ name: c.name, roleTitle: c.roleTitle ?? "", email: c.email ?? "", phoneNumber: c.phoneNumber ?? "", receivesWorkOrderEmails: c.receivesWorkOrderEmails });
        setModalError(null);
        setModal("edit");
    };
    const openEditJob = (c: ProjectContact) => {
        setModalScope("job");
        setEditJobTarget(c);
        setDraft({ name: c.name, roleTitle: c.roleTitle ?? "", email: c.email ?? "", phoneNumber: c.phoneNumber ?? "", receivesWorkOrderEmails: c.receivesWorkOrderEmails });
        setModalError(null);
        setModal("edit");
    };
    const [deleteJobTarget, setDeleteJobTarget] = useState<ProjectContact | null>(null);

    const draftValid = draft.name.trim().length > 0 && (!draft.email.trim() || EMAIL_RE.test(draft.email.trim()));

    const refreshAfterWrite = async () => {
        if (selectedID != null) await fetchContacts(selectedID);
        await fetchCustomers();
    };

    const handleSave = async () => {
        if (!draftValid || saving || (modalScope === "builder" && selectedID == null)) return;
        setSaving(true);
        setModalError(null);
        const body = {
            name: draft.name.trim(),
            roleTitle: draft.roleTitle.trim() || null,
            email: draft.email.trim() || null,
            phoneNumber: draft.phoneNumber.trim() || null,
            receivesWorkOrderEmails: draft.receivesWorkOrderEmails,
        };
        try {
            if (modalScope === "job") {
                if (selectedProjectID == null) return;
                if (modal === "edit" && editJobTarget) {
                    await updateProjectContact(editJobTarget.projectContactID, body);
                } else {
                    await createProjectContact(selectedProjectID, body);
                }
                setModal(null);
                await fetchProjectContacts(selectedProjectID);
            } else {
                if (modal === "edit" && editTarget) {
                    await updateCustomerContact(editTarget.customerContactID, body);
                } else {
                    if (selectedID == null) return;
                    await createCustomerContact(selectedID, body);
                }
                setModal(null);
                await refreshAfterWrite();
            }
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to save contact.");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteJob = async () => {
        if (!deleteJobTarget || saving || selectedProjectID == null) return;
        setSaving(true);
        try {
            await deleteProjectContact(deleteJobTarget.projectContactID);
            setDeleteJobTarget(null);
            await fetchProjectContacts(selectedProjectID);
        } catch (err: any) {
            setProjectError(err?.response?.data?.message || err?.message || "Failed to remove contact.");
            setDeleteJobTarget(null);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget || saving) return;
        setSaving(true);
        try {
            await deleteCustomerContact(deleteTarget.customerContactID);
            setDeleteTarget(null);
            await refreshAfterWrite();
        } catch (err: any) {
            setContactsError(err?.response?.data?.message || err?.message || "Failed to remove contact.");
            setDeleteTarget(null);
        } finally {
            setSaving(false);
        }
    };

    const field = (label: string, key: "name" | "roleTitle" | "email" | "phoneNumber", placeholder: string, required = false) => (
        <div className="field">
            <label>{label}{required ? " *" : ""}</label>
            <input
                className="input"
                value={draft[key]}
                placeholder={placeholder}
                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                disabled={saving}
            />
        </div>
    );

    return (
        <div className="wov2">
            <PageHead
                Icon={BookUser}
                title="Builder Contacts"
                sub="Who we can actually reach at each builder — PMs, assistants, supers. Feeds work order emails."
            />

            {error && (
                <Banner variant="danger" Icon={AlertTriangle} action={<button className="btn sm" onClick={fetchCustomers} type="button">Retry</button>}>
                    {error}
                </Banner>
            )}

            <div className="dash-two">
                <div>
                    <SectionLabel Icon={Users}>Builders</SectionLabel>
                    <div className="card">
                        <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--border, #e6e8ec)" }}>
                            <input
                                className="input"
                                placeholder="Search builder name or #"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                            />
                        </div>
                        <div style={{ maxHeight: 520, overflowY: "auto" }}>
                            {loading ? (
                                <div className="muted" style={{ padding: 20, textAlign: "center" }}>Loading…</div>
                            ) : filtered.length === 0 ? (
                                <div className="muted" style={{ padding: 20, textAlign: "center" }}>No builders match.</div>
                            ) : (
                                filtered.map((c) => (
                                    <button
                                        key={c.customerID}
                                        type="button"
                                        onClick={() => setSelectedID(c.customerID)}
                                        style={{
                                            display: "flex", width: "100%", alignItems: "center", gap: 10,
                                            padding: "10px 14px", border: "none", cursor: "pointer", textAlign: "left",
                                            background: c.customerID === selectedID ? "var(--primary-tint, #e8effd)" : "transparent",
                                            borderBottom: "1px solid var(--border, #eef0f3)",
                                        }}
                                    >
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                            <span className="strong" style={{ display: "block" }}>{c.name || `Customer #${c.customerID}`}</span>
                                            {c.altCustomerID && <span className="sub mono">#{c.altCustomerID}</span>}
                                        </span>
                                        <span className={`pill ${c.contactCount > 0 ? "ok" : "neutral"}`}>
                                            {c.contactCount} contact{c.contactCount === 1 ? "" : "s"}
                                        </span>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                <div>
                    <SectionLabel Icon={BookUser}>
                        {selected ? `Contacts · ${selected.name ?? `Customer #${selected.customerID}`}` : "Contacts"}
                    </SectionLabel>
                    <div className={`card${!selected || contactsLoading || contacts.length === 0 ? " pad" : ""}`}>
                        {!selected ? (
                            <EmptyState Icon={BookUser} title="Pick a builder">
                                Choose a builder on the left to see and manage its contacts.
                            </EmptyState>
                        ) : contactsLoading ? (
                            <div className="muted" style={{ textAlign: "center" }}>Loading contacts…</div>
                        ) : contactsError ? (
                            <Banner variant="danger" Icon={AlertTriangle}>{contactsError}</Banner>
                        ) : (
                            <>
                                {contacts.length === 0 ? (
                                    <EmptyState Icon={BookUser} title="No contacts yet">
                                        {canManage ? "Add the PM, assistant PM or super for this builder." : "Nothing recorded for this builder yet."}
                                    </EmptyState>
                                ) : (
                                    <div className="tbl-wrap">
                                        <table className="dt">
                                            <thead>
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Role</th>
                                                    <th>Email</th>
                                                    <th>Phone</th>
                                                    {canManage && <th></th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {contacts.map((c) => (
                                                    <tr key={c.customerContactID}>
                                                        <td className="strong">{c.name}{c.receivesWorkOrderEmails && <span className="pill info" style={{ marginLeft: 6 }}>WO emails</span>}</td>
                                                        <td>{c.roleTitle || <span className="dash">–</span>}</td>
                                                        <td>{c.email ? <span className="mono"><Mail size={12} /> {c.email}</span> : <span className="dash">–</span>}</td>
                                                        <td>{c.phoneNumber ? <span className="mono"><Phone size={12} /> {c.phoneNumber}</span> : <span className="dash">–</span>}</td>
                                                        {canManage && (
                                                            <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                                                                <button className="btn sm ghost" type="button" title="Edit" onClick={() => openEdit(c)}><Pencil size={13} /></button>
                                                                <button className="btn sm ghost" type="button" title="Remove" onClick={() => setDeleteTarget(c)}><Trash2 size={13} /></button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                {canManage && (
                                    <div style={{ padding: contacts.length === 0 ? 0 : "10px 14px" }}>
                                        <button className="btn sm" type="button" onClick={() => openAdd("builder")}>
                                            <Plus size={14} /> Add contact
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* TAS-296 — per-job copies of the builder's contacts */}
                    {selected && (
                        <>
                            <SectionLabel Icon={BookUser} style={{ marginTop: 18 }}>Job contacts</SectionLabel>
                            <div className="card pad">
                                <div className="field" style={{ maxWidth: 420 }}>
                                    <label>Job</label>
                                    <select
                                        className="input"
                                        value={selectedProjectID ?? ""}
                                        onChange={(e) => setSelectedProjectID(e.target.value ? Number(e.target.value) : null)}
                                    >
                                        <option value="">— pick a job —</option>
                                        {projects.map((pr) => (
                                            <option key={pr.projectID} value={pr.projectID}>
                                                {[pr.altProjectID, pr.name].filter(Boolean).join(" · ")}{pr.active ? "" : " (inactive)"}
                                            </option>
                                        ))}
                                    </select>
                                    <span className="sub" style={{ marginTop: 4 }}>
                                        Opening a job for the first time copies the builder's contacts onto it; after that the job's list is its own.
                                    </span>
                                </div>

                                {selectedProjectID != null && (
                                    projectLoading ? (
                                        <div className="muted">Loading job contacts…</div>
                                    ) : projectError ? (
                                        <Banner variant="danger" Icon={AlertTriangle}>{projectError}</Banner>
                                    ) : (
                                        <>
                                            {projectSeeded && (
                                                <p className="muted" style={{ marginTop: 0 }}>
                                                    Pre-filled from {selected.name ?? "the builder"} just now — edit freely, the builder defaults stay untouched.
                                                </p>
                                            )}
                                            {projectContacts.length === 0 ? (
                                                <div className="muted">No contacts on this job.</div>
                                            ) : (
                                                <div className="tbl-wrap">
                                                    <table className="dt">
                                                        <thead>
                                                            <tr>
                                                                <th>Name</th>
                                                                <th>Role</th>
                                                                <th>Email</th>
                                                                <th>Phone</th>
                                                                {canManage && <th></th>}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {projectContacts.map((c) => (
                                                                <tr key={c.projectContactID}>
                                                                    <td className="strong">{c.name}{c.receivesWorkOrderEmails && <span className="pill info" style={{ marginLeft: 6 }}>WO emails</span>}</td>
                                                                    <td>{c.roleTitle || <span className="dash">–</span>}</td>
                                                                    <td>{c.email ? <span className="mono">{c.email}</span> : <span className="dash">–</span>}</td>
                                                                    <td>{c.phoneNumber ? <span className="mono">{c.phoneNumber}</span> : <span className="dash">–</span>}</td>
                                                                    {canManage && (
                                                                        <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                                                                            <button className="btn sm ghost" type="button" title="Edit" onClick={() => openEditJob(c)}><Pencil size={13} /></button>
                                                                            <button className="btn sm ghost" type="button" title="Remove" onClick={() => setDeleteJobTarget(c)}><Trash2 size={13} /></button>
                                                                        </td>
                                                                    )}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                            {canManage && (
                                                <div style={{ marginTop: 10 }}>
                                                    <button className="btn sm" type="button" onClick={() => openAdd("job")}>
                                                        <Plus size={14} /> Add job contact
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {modal && selected && (
                <ModalShell
                    Icon={BookUser}
                    title={`${modal === "edit" ? "Edit" : "Add"} ${modalScope === "job" ? "Job " : ""}Contact`}
                    sub={modalScope === "job"
                        ? (projects.find((pr) => pr.projectID === selectedProjectID)
                            ? [projects.find((pr) => pr.projectID === selectedProjectID)!.altProjectID, projects.find((pr) => pr.projectID === selectedProjectID)!.name].filter(Boolean).join(" · ")
                            : "Job")
                        : selected.name ?? `Customer #${selected.customerID}`}
                    onClose={() => !saving && setModal(null)}
                    footer={
                        <>
                            <button className="btn ghost" onClick={() => setModal(null)} disabled={saving}>Cancel</button>
                            <button className="btn primary" onClick={handleSave} disabled={saving || !draftValid}>
                                {saving ? "Saving…" : "Save contact"}
                            </button>
                        </>
                    }
                >
                    {field("Name", "name", "e.g. Jane Rivera", true)}
                    {field("Role / Title", "roleTitle", "e.g. Project Manager")}
                    {field("Email", "email", "name@builder.com")}
                    {field("Phone", "phoneNumber", "(951) 555-0123")}
                    <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={draft.receivesWorkOrderEmails}
                            onChange={(e) => setDraft((d) => ({ ...d, receivesWorkOrderEmails: e.target.checked }))}
                            disabled={saving}
                        />
                        <span>Receives work order emails <span className="muted">(pre-selected in the send dialog)</span></span>
                    </label>
                    {draft.email.trim() && !EMAIL_RE.test(draft.email.trim()) && (
                        <p className="muted">Enter a valid email address or leave it blank.</p>
                    )}
                    {modalError && <Banner variant="danger" Icon={AlertTriangle}>{modalError}</Banner>}
                </ModalShell>
            )}

            {deleteJobTarget && (
                <ModalShell
                    Icon={Trash2}
                    danger
                    title="Remove Job Contact"
                    sub={deleteJobTarget.name}
                    onClose={() => !saving && setDeleteJobTarget(null)}
                    footer={
                        <>
                            <button className="btn ghost" onClick={() => setDeleteJobTarget(null)} disabled={saving}>Cancel</button>
                            <button className="btn danger" onClick={handleDeleteJob} disabled={saving}>
                                {saving ? "Removing…" : "Remove contact"}
                            </button>
                        </>
                    }
                >
                    <p>Remove <b>{deleteJobTarget.name}</b> from this job only? The builder's defaults are untouched.</p>
                </ModalShell>
            )}

            {deleteTarget && (
                <ModalShell
                    Icon={Trash2}
                    danger
                    title="Remove Contact"
                    sub={deleteTarget.name}
                    onClose={() => !saving && setDeleteTarget(null)}
                    footer={
                        <>
                            <button className="btn ghost" onClick={() => setDeleteTarget(null)} disabled={saving}>Cancel</button>
                            <button className="btn danger" onClick={handleDelete} disabled={saving}>
                                {saving ? "Removing…" : "Remove contact"}
                            </button>
                        </>
                    }
                >
                    <p>Remove <b>{deleteTarget.name}</b> from {selected?.name ?? "this builder"}? Jobs already using this contact keep their copy.</p>
                </ModalShell>
            )}
        </div>
    );
}
